import { createApp } from '@backstage/frontend-defaults';
import { convertLegacyAppOptions } from '@backstage/core-compat-api';
import {
  signInPage,
  analyticsApi,
  scmAuthApiFactory,
  scmIntegrationsApiFactory,
  lightThemeExtension,
  darkThemeExtension,
} from './extensions';
import {
  createFrontendModule,
  createFrontendPlugin,
} from '@backstage/frontend-plugin-api';
import { shortcutsPlugin } from '@backstage-community/plugin-shortcuts';
import { SidebarContent } from './modules/sidebar/sidebar';
import {
  apiDocsPluginOverride,
  catalogImportPluginOverride,
  catalogPluginOverride,
  devToolsPluginOverride,
  frontendExtensionsExplorerPluginOverride,
  homePluginOverride,
  orgPluginOverride,
  qetaPluginOverride,
  rbacPluginOverride,
  scaffolderPluginOverride,
  searchPluginOverride,
  techdocsPluginOverride,
  userSettingsPluginOverride,
} from './overrides';
import { techDocsReportIssueAddonModule } from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import toolOnboardingDashboard from '@internal/backstage-plugin-tool-onboarding-dashboard';
import techDocsMermaidAddonModule from 'backstage-plugin-techdocs-addon-mermaid/alpha';
import techDocsInteractiveTableAddonModule from '@internal/backstage-plugin-techdocs-interactive-table/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import landingPagesPlugin, {
  agentsLandingPagePlugin,
  contributeLandingPagePlugin,
  dashboardsLandingPagePlugin,
  exploreLandingPagePlugin,
  supportLandingPagePlugin,
} from '@internal/backstage-plugin-landing-pages';
import bobPlugin from '@internal/backstage-plugin-bob';
import techRadarPlugin from '@internal/backstage-plugin-tech-radar';
import techSpotEventCalendarPlugin from '@internal/backstage-plugin-techspot-event-calendar';
import decisionMatrixPlugin from '@hm-group/backstage-plugin-decision-matrix';
import managedPlatformPickerModule from '@hm-group/backstage-plugin-dp-managed-platform-picker';
import qetaPlugin from '@drodil/backstage-plugin-qeta';
import sonarOnboardingPlugin from '@hm-group/backstage-plugin-sonar-onboarding';
import { moduleFederationLoader } from './module-federation';
import authPlugin from '@backstage/plugin-auth';
import './styles.css';
import { adoptionAnalyticsPluginOverride } from './overrides/adoption-analytics-plugin';

const convertedOptionsModule = convertLegacyAppOptions({
  plugins: [shortcutsPlugin], // @NFS: as of their lack of support for the new frontend module system
});

const app = createApp({
  bindRoutes({ bind }) {
    bind(landingPagesPlugin.externalRoutes, {
      contribute: contributeLandingPagePlugin.routes.root,
      decisionMatrix: decisionMatrixPlugin.routes.root,
      createComponent: scaffolderPlugin.routes.root,
      registerComponent: catalogImportPlugin.routes.importPage,
      qeta: qetaPlugin.routes.root,
      techRadar: techRadarPlugin.routes.root,
      techSpotEventCalendar: techSpotEventCalendarPlugin.routes.root,
    });
  },
  features: [
    authPlugin,
    toolOnboardingDashboard,
    moduleFederationLoader,
    catalogImportPluginOverride,
    catalogPluginOverride,
    orgPluginOverride,
    techdocsPluginOverride,
    techDocsReportIssueAddonModule,
    techDocsMermaidAddonModule,
    techDocsInteractiveTableAddonModule,
    homePluginOverride,
    searchPluginOverride,
    convertedOptionsModule,
    userSettingsPluginOverride,
    devToolsPluginOverride,
    frontendExtensionsExplorerPluginOverride,
    qetaPluginOverride,
    rbacPluginOverride,
    scaffolderPluginOverride,
    apiDocsPluginOverride,
    exploreLandingPagePlugin,
    contributeLandingPagePlugin,
    dashboardsLandingPagePlugin,
    supportLandingPagePlugin,
    agentsLandingPagePlugin,
    bobPlugin,
    sonarOnboardingPlugin,
    adoptionAnalyticsPluginOverride,
    managedPlatformPickerModule,
    createFrontendModule({
      pluginId: 'app',
      extensions: [
        signInPage,
        // @NFS converted from themes - sort order matters :-)
        lightThemeExtension,
        darkThemeExtension,
        // @NFS converted from apis
        analyticsApi,
        scmIntegrationsApiFactory,
        scmAuthApiFactory,
        SidebarContent,
      ],
    }),
    // @NFS converted from featureFlags
    createFrontendPlugin({
      pluginId: 'hm-feature-flags',
      featureFlags: [
        { name: 'threaddy' },
        { name: 'hide-notification-badge-on-logo' },
        { name: 'catalog-owned-filter' },
        { name: 'lens' },
      ],
    }),
  ],
});

export default app.createRoot();
